.header {
  position: sticky;
  top: 0;
  z-index: var(--z-navbar);
  background: var(--color-bg-sunken);
  border-bottom: var(--border-width-2) var(--border-style) var(--color-border);
  /* Hard pixel "shadow" beneath the bar — no blur. */
  box-shadow: 0 var(--border-width-1) 0 0 var(--color-bg);
}

.inner {
  /* Three-track grid (brand | nav | toggle) so the nav stays optically centered
     in the viewport even when brand and toggle widths differ. */
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: var(--space-4);
  max-width: var(--content-max-width);
  margin: 0 auto;
  padding: var(--space-3) var(--section-padding-inline);
}

/* Skip-link: visually hidden until focused, then floats above the navbar. */
.skipLink {
  position: absolute;
  top: var(--space-2);
  left: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: var(--color-accent);
  color: var(--color-bg);
  font-family: var(--font-display);
  font-size: 0.625rem;
  letter-spacing: var(--letter-spacing-display);
  text-decoration: none;
  border: var(--border-width-1) var(--border-style) var(--color-border);
  box-shadow: var(--shadow-pixel-sm);
  transform: translateY(-200%);
  transition: transform var(--transition-fast);
  z-index: calc(var(--z-navbar) + 1);
}

.skipLink:focus-visible {
  transform: translateY(0);
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-display);
  font-size: 0.75rem; /* 12px chunky brand mark */
  letter-spacing: var(--letter-spacing-display);
  color: var(--color-fg);
  text-decoration: none;
  white-space: nowrap;
}

.brand:hover,
.brand:focus-visible {
  color: var(--color-accent-strong);
}

.brandSprite {
  display: inline-block;
  color: var(--color-accent);
  font-size: 1rem;
  line-height: 1;
  /* Mimic a tiny blinking sprite without needing an asset. */
  animation: brandPulse 1.6s steps(2, end) infinite;
}

@keyframes brandPulse {
  0%,
  100% {
    color: var(--color-accent);
  }
  50% {
    color: var(--color-magenta);
  }
}

.brandText {
  /* Keep the wordmark visible on tablet+ but trim on the smallest screens. */
}

.nav {
  display: flex;
  justify-content: center;
  min-width: 0;
}

.list {
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  gap: var(--space-5);
  margin: 0;
  padding: 0;
  list-style: none;
}

.link {
  display: inline-flex;
  align-items: center;
  font-family: var(--font-display);
  font-size: 0.625rem; /* 10px nav items */
  letter-spacing: var(--letter-spacing-display);
  color: var(--color-fg);
  text-decoration: none;
  padding: var(--space-3) var(--space-3);
  min-height: 44px; /* WCAG 2.5.5 target size — comfy for touch + large cursors */
  border-bottom: var(--border-width-1) var(--border-style) transparent;
  white-space: nowrap;
  transition: color var(--transition-fast),
    border-color var(--transition-fast);
}

.link:hover,
.link:focus-visible {
  color: var(--color-accent);
  border-bottom-color: var(--color-accent);
}

.toggleSlot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

@media (max-width: 820px) {
  /* Tablet & down: collapse the three-track grid into a two-row flex layout so
     the nav can take the full row and stop competing with the brand + toggle
     for the same horizontal slot. Without this, 768px viewports overflow
     because brand + 5 nav links + toggle exceed the inner width. */
  .inner {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  /* Order: brand → toggle → nav (nav drops to its own row) */
  .brand {
    order: 1;
  }

  .toggleSlot {
    order: 2;
    margin-left: auto;
  }

  .nav {
    order: 3;
    flex-basis: 100%;
    justify-content: center;
  }

  .list {
    gap: var(--space-3);
    justify-content: center;
  }

  .link {
    padding: var(--space-3) var(--space-2);
  }
}

@media (max-width: 600px) {
  .inner {
    padding-inline: var(--space-3);
    gap: var(--space-2);
  }

  .list {
    gap: var(--space-1);
  }

  .link {
    padding: var(--space-3) var(--space-1);
    font-size: 0.5rem;
    letter-spacing: 0.02em;
  }
}

@media (max-width: 360px) {
  .inner {
    padding-inline: var(--space-2);
  }

  .list {
    gap: 2px;
  }

  .link {
    padding: var(--space-3) 2px;
  }
}
